import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  riskApi,
  mitigationApi,
  suggestionApi,
  catalogApi,
  type RiskFilters,
  type MitigationFilters,
  type SuggestionContext,
} from '../services/risk.service';
import type {
  Risk,
  CreateRiskDto,
  UpdateRiskDto,
  Mitigation,
  CreateMitigationDto,
  UpdateMitigationDto,
} from 'shared';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const riskQueryKeys = {
  all: ['risks'] as const,
  lists: () => [...riskQueryKeys.all, 'list'] as const,
  list: (filters?: RiskFilters) => [...riskQueryKeys.lists(), filters] as const,
  byComponent: (componentId: string) =>
    [...riskQueryKeys.all, 'component', componentId] as const,
  bySpace: (spaceId: string) =>
    [...riskQueryKeys.all, 'space', spaceId] as const,
  detail: (id: string) => [...riskQueryKeys.all, 'detail', id] as const,
};

export const mitigationQueryKeys = {
  all: ['mitigations'] as const,
  lists: () => [...mitigationQueryKeys.all, 'list'] as const,
  list: (filters?: MitigationFilters) =>
    [...mitigationQueryKeys.lists(), filters] as const,
  byRisk: (riskId: string) =>
    [...mitigationQueryKeys.all, 'risk', riskId] as const,
  detail: (id: string) => [...mitigationQueryKeys.all, 'detail', id] as const,
};

export const suggestionQueryKeys = {
  all: ['suggestions'] as const,
  forComponent: (context: SuggestionContext) =>
    [...suggestionQueryKeys.all, 'component', context] as const,
};

export const catalogQueryKeys = {
  attack: {
    tactics: ['catalog', 'attack', 'tactics'] as const,
    techniques: (tacticId?: string) =>
      ['catalog', 'attack', 'techniques', tacticId] as const,
    technique: (id: string) => ['catalog', 'attack', 'technique', id] as const,
    search: (query: string) => ['catalog', 'attack', 'search', query] as const,
  },
  d3fend: {
    tactics: ['catalog', 'd3fend', 'tactics'] as const,
    techniques: (tacticId?: string) =>
      ['catalog', 'd3fend', 'techniques', tacticId] as const,
    technique: (id: string) => ['catalog', 'd3fend', 'technique', id] as const,
    counters: (attackId: string) =>
      ['catalog', 'd3fend', 'counters', attackId] as const,
    search: (query: string) => ['catalog', 'd3fend', 'search', query] as const,
  },
  iso: {
    categories: ['catalog', 'iso', 'categories'] as const,
    controls: (categoryId?: string, cia?: string) =>
      ['catalog', 'iso', 'controls', categoryId, cia] as const,
    control: (id: string) => ['catalog', 'iso', 'control', id] as const,
    search: (query: string) => ['catalog', 'iso', 'search', query] as const,
  },
  riskProfiles: {
    all: ['catalog', 'risk-profiles'] as const,
    byType: (type: string) => ['catalog', 'risk-profiles', type] as const,
  },
};

// =============================================================================
// RISK HOOKS
// =============================================================================

/**
 * Hook to fetch all risks with optional filters
 * Note: spaceId is required by the API, so we disable the query if not provided
 */
export function useRisks(filters?: RiskFilters) {
  return useQuery({
    queryKey: riskQueryKeys.list(filters),
    queryFn: () => riskApi.getAll(filters),
    enabled: !!filters?.spaceId, // Only fetch when spaceId is provided
  });
}

/**
 * Hook to fetch risks for a specific component
 */
export function useRisksByComponent(componentId: string | undefined) {
  return useQuery({
    queryKey: riskQueryKeys.byComponent(componentId ?? ''),
    queryFn: () => riskApi.getByComponent(componentId ?? ''),
    enabled: !!componentId,
  });
}

/**
 * Hook to fetch risks for a specific space
 */
export function useRisksBySpace(spaceId: string | undefined) {
  return useQuery({
    queryKey: riskQueryKeys.bySpace(spaceId ?? ''),
    queryFn: () => riskApi.getBySpace(spaceId ?? ''),
    enabled: !!spaceId,
  });
}

/**
 * Hook to fetch a single risk by ID
 */
export function useRisk(id: string | undefined) {
  return useQuery({
    queryKey: riskQueryKeys.detail(id ?? ''),
    queryFn: () => riskApi.getById(id ?? ''),
    enabled: !!id,
  });
}

/**
 * Hook to create a new risk
 */
export function useCreateRisk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRiskDto) => riskApi.create(data),
    onSuccess: newRisk => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.all });

      // Optimistically add to component-specific cache
      if (newRisk.componentId) {
        queryClient.setQueryData<Risk[]>(
          riskQueryKeys.byComponent(newRisk.componentId),
          old => (old ? [...old, newRisk] : [newRisk])
        );
      }
    },
  });
}

/**
 * Hook to update an existing risk
 */
export function useUpdateRisk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRiskDto }) =>
      riskApi.update(id, data),
    onSuccess: updatedRisk => {
      // Update the detail cache
      queryClient.setQueryData(
        riskQueryKeys.detail(updatedRisk.id),
        updatedRisk
      );

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.lists() });
    },
  });
}

/**
 * Hook to delete a risk
 */
export function useDeleteRisk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => riskApi.delete(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: riskQueryKeys.detail(deletedId),
      });

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.all });
    },
  });
}

// =============================================================================
// MITIGATION HOOKS
// =============================================================================

/**
 * Hook to fetch all mitigations with optional filters
 */
export function useMitigations(filters?: MitigationFilters) {
  return useQuery({
    queryKey: mitigationQueryKeys.list(filters),
    queryFn: () => mitigationApi.getAll(filters),
  });
}

/**
 * Hook to fetch mitigations for a specific risk
 */
export function useMitigationsByRisk(riskId: string | undefined) {
  return useQuery({
    queryKey: mitigationQueryKeys.byRisk(riskId ?? ''),
    queryFn: () => mitigationApi.getByRisk(riskId ?? ''),
    enabled: !!riskId,
  });
}

/**
 * Hook to fetch a single mitigation by ID
 */
export function useMitigation(id: string | undefined) {
  return useQuery({
    queryKey: mitigationQueryKeys.detail(id ?? ''),
    queryFn: () => mitigationApi.getById(id ?? ''),
    enabled: !!id,
  });
}

/**
 * Hook to create a new mitigation
 */
export function useCreateMitigation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMitigationDto) => mitigationApi.create(data),
    onSuccess: newMitigation => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: mitigationQueryKeys.all });

      // Optimistically add to risk-specific cache
      if (newMitigation.riskId) {
        queryClient.setQueryData<Mitigation[]>(
          mitigationQueryKeys.byRisk(newMitigation.riskId),
          old => (old ? [...old, newMitigation] : [newMitigation])
        );
      }

      // Invalidate the associated risk to recalculate residual risk
      queryClient.invalidateQueries({
        queryKey: riskQueryKeys.detail(newMitigation.riskId),
      });
    },
  });
}

/**
 * Hook to update an existing mitigation
 */
export function useUpdateMitigation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMitigationDto }) =>
      mitigationApi.updateMitigation(id, data),
    onSuccess: (updatedMitigation: Mitigation) => {
      // Update the detail cache
      queryClient.setQueryData(
        mitigationQueryKeys.detail(updatedMitigation.id),
        updatedMitigation
      );

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: mitigationQueryKeys.lists() });

      // Invalidate the associated risk to recalculate residual risk
      queryClient.invalidateQueries({
        queryKey: riskQueryKeys.detail(updatedMitigation.riskId),
      });
    },
  });
}

/**
 * Hook to delete a mitigation
 */
export function useDeleteMitigation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => mitigationApi.delete(id),
    onSuccess: (_, deletedId) => {
      // Get the mitigation from cache to find the associated risk
      const mitigation = queryClient.getQueryData<Mitigation>(
        mitigationQueryKeys.detail(deletedId)
      );

      // Remove from cache
      queryClient.removeQueries({
        queryKey: mitigationQueryKeys.detail(deletedId),
      });

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: mitigationQueryKeys.all });

      // Invalidate the associated risk to recalculate residual risk
      if (mitigation?.riskId) {
        queryClient.invalidateQueries({
          queryKey: riskQueryKeys.detail(mitigation.riskId),
        });
      }
    },
  });
}

// =============================================================================
// SUGGESTION HOOKS
// =============================================================================

/**
 * Hook to fetch suggestions for a component
 */
export function useSuggestions(context: SuggestionContext | undefined) {
  const defaultContext: SuggestionContext = {
    componentId: '',
    spaceId: '',
  };
  const effectiveContext = context ?? defaultContext;

  return useQuery({
    queryKey: context ? suggestionQueryKeys.forComponent(context) : [],
    queryFn: () => suggestionApi.getForComponent(effectiveContext),
    enabled: !!context?.componentId && !!context?.spaceId,
    staleTime: 30000, // Suggestions are valid for 30 seconds
  });
}

// =============================================================================
// CATALOG HOOKS - MITRE ATT&CK
// =============================================================================

/**
 * Hook to fetch all ATT&CK tactics
 */
export function useAttackTactics() {
  return useQuery({
    queryKey: catalogQueryKeys.attack.tactics,
    queryFn: () => catalogApi.attack.getTactics(),
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to fetch ATT&CK techniques (optionally filtered by tactic)
 */
export function useAttackTechniques(tacticId?: string) {
  return useQuery({
    queryKey: catalogQueryKeys.attack.techniques(tacticId),
    queryFn: () => catalogApi.attack.getTechniques(tacticId),
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to fetch a single ATT&CK technique by ID
 */
export function useAttackTechnique(id: string | undefined) {
  return useQuery({
    queryKey: catalogQueryKeys.attack.technique(id ?? ''),
    queryFn: () => catalogApi.attack.getTechniqueById(id ?? ''),
    enabled: !!id,
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to search ATT&CK techniques
 */
export function useAttackSearch(query: string) {
  return useQuery({
    queryKey: catalogQueryKeys.attack.search(query),
    queryFn: () => catalogApi.attack.search(query),
    enabled: query.length >= 2,
    staleTime: 60000, // Cache searches for 1 minute
  });
}

// =============================================================================
// CATALOG HOOKS - MITRE D3FEND
// =============================================================================

/**
 * Hook to fetch all D3FEND tactics
 */
export function useD3fendTactics() {
  return useQuery({
    queryKey: catalogQueryKeys.d3fend.tactics,
    queryFn: () => catalogApi.d3fend.getTactics(),
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to fetch D3FEND techniques (optionally filtered by tactic)
 */
export function useD3fendTechniques(tacticId?: string) {
  return useQuery({
    queryKey: catalogQueryKeys.d3fend.techniques(tacticId),
    queryFn: () => catalogApi.d3fend.getTechniques(tacticId),
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to fetch a single D3FEND technique by ID
 */
export function useD3fendTechnique(id: string | undefined) {
  return useQuery({
    queryKey: catalogQueryKeys.d3fend.technique(id ?? ''),
    queryFn: () => catalogApi.d3fend.getTechniqueById(id ?? ''),
    enabled: !!id,
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to fetch D3FEND countermeasures for an ATT&CK technique
 */
export function useD3fendCounters(attackId: string | undefined) {
  return useQuery({
    queryKey: catalogQueryKeys.d3fend.counters(attackId ?? ''),
    queryFn: () => catalogApi.d3fend.getCountersForAttack(attackId ?? ''),
    enabled: !!attackId,
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to search D3FEND techniques
 */
export function useD3fendSearch(query: string) {
  return useQuery({
    queryKey: catalogQueryKeys.d3fend.search(query),
    queryFn: () => catalogApi.d3fend.search(query),
    enabled: query.length >= 2,
    staleTime: 60000, // Cache searches for 1 minute
  });
}

// =============================================================================
// CATALOG HOOKS - ISO 27001:2022
// =============================================================================

/**
 * Hook to fetch all ISO 27001:2022 control categories
 */
export function useIsoCategories() {
  return useQuery({
    queryKey: catalogQueryKeys.iso.categories,
    queryFn: () => catalogApi.iso.getCategories(),
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to fetch ISO 27001:2022 controls (optionally filtered)
 */
export function useIsoControls(
  categoryId?: string,
  cia?: 'confidentiality' | 'integrity' | 'availability'
) {
  return useQuery({
    queryKey: catalogQueryKeys.iso.controls(categoryId, cia),
    queryFn: () => catalogApi.iso.getControls(categoryId, cia),
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to fetch a single ISO 27001:2022 control by ID
 */
export function useIsoControl(id: string | undefined) {
  return useQuery({
    queryKey: catalogQueryKeys.iso.control(id ?? ''),
    queryFn: () => catalogApi.iso.getControlById(id ?? ''),
    enabled: !!id,
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to search ISO 27001:2022 controls
 */
export function useIsoSearch(query: string) {
  return useQuery({
    queryKey: catalogQueryKeys.iso.search(query),
    queryFn: () => catalogApi.iso.search(query),
    enabled: query.length >= 2,
    staleTime: 60000, // Cache searches for 1 minute
  });
}

// =============================================================================
// CATALOG HOOKS - RISK PROFILES
// =============================================================================

/**
 * Hook to fetch all component risk profiles
 */
export function useRiskProfiles() {
  return useQuery({
    queryKey: catalogQueryKeys.riskProfiles.all,
    queryFn: () => catalogApi.riskProfiles.getAll(),
    staleTime: Infinity, // Static data
  });
}

/**
 * Hook to fetch a risk profile by component type
 */
export function useRiskProfileByType(componentType: string | undefined) {
  return useQuery({
    queryKey: catalogQueryKeys.riskProfiles.byType(componentType ?? ''),
    queryFn: () => catalogApi.riskProfiles.getByType(componentType ?? ''),
    enabled: !!componentType,
    staleTime: Infinity, // Static data
  });
}

// =============================================================================
// AI HOOKS
// =============================================================================

import { aiApi, type AiAnalysisContext } from '../services/risk.service';

export const aiQueryKeys = {
  status: ['ai', 'status'] as const,
  analysis: (context: AiAnalysisContext) =>
    ['ai', 'analysis', context] as const,
};

/**
 * Hook to check AI service status
 */
export function useAiStatus() {
  return useQuery({
    queryKey: aiQueryKeys.status,
    queryFn: () => aiApi.getStatus(),
    staleTime: 60000, // Check every minute
  });
}

/**
 * Hook to perform AI analysis on a component
 */
export function useAiAnalysis() {
  return useMutation({
    mutationFn: (context: AiAnalysisContext) => aiApi.analyzeComponent(context),
  });
}

/**
 * Hook to perform security analysis
 */
export function useSecurityAnalysis() {
  return useMutation({
    mutationFn: ({
      componentId,
      spaceId,
      includeRelations,
    }: {
      componentId: string;
      spaceId: string;
      includeRelations?: boolean;
    }) => aiApi.securityAnalysis(componentId, spaceId, includeRelations),
  });
}

/**
 * Hook to perform attack simulation
 */
export function useAttackSimulation() {
  return useMutation({
    mutationFn: ({
      componentId,
      spaceId,
      includeRelations,
      maxDepth,
    }: {
      componentId: string;
      spaceId: string;
      includeRelations?: boolean;
      maxDepth?: number;
    }) =>
      aiApi.attackSimulation(componentId, spaceId, includeRelations, maxDepth),
  });
}
